import { createContext, useContext } from "react"

/**
 * Marks values that came from somewhere other than the borrower typing them,
 * so a wrong one cannot pass as something they entered.
 *
 * Fed the source the values came from rather than a list of field names, so
 * it keeps working as more of the profile gets looked up for us. `FormLabel`
 * and `FormControl` read it, so no form needs changes of its own.
 *
 *     <PrefilledFields values={prefilledFrom(partialBusinessDetails)}>
 *       <BusinessDetailsForm />
 *     </PrefilledFields>
 */
export interface PrefilledEntry {
  /** Says what is missing, where only part of a value is known. */
  note?: string
}

export type PrefilledMap = Record<string, PrefilledEntry>

const PrefilledFieldsContext = createContext<PrefilledMap>({})

interface Props {
  values: PrefilledMap
  children: React.ReactNode
}

export function PrefilledFields({ values, children }: Props) {
  return (
    <PrefilledFieldsContext.Provider value={values}>
      {children}
    </PrefilledFieldsContext.Provider>
  )
}

/**
 * Builds the map from whatever object the values arrived in.
 *
 * `null`, `undefined`, `""` and `false` are skipped — but not `0`, which can
 * be a real answer. `false` because a partial model's boolean is false whether
 * or not anyone told us anything, so marking an unchecked box would claim
 * knowledge we do not have.
 *
 * `prefix` matches how the form nests its fields, e.g. `businessDetails` for
 * a field registered as `businessDetails.industry`.
 */
export function prefilledFrom(
  // `object`, not an index signature, so generated API models pass in as-is.
  source: object | null | undefined,
  { prefix, notes }: { prefix?: string; notes?: Record<string, string> } = {},
): PrefilledMap {
  if (!source) {
    return {}
  }

  return Object.entries(source).reduce<PrefilledMap>((map, [key, value]) => {
    if (
      value === null ||
      value === undefined ||
      value === "" ||
      value === false
    ) {
      return map
    }

    map[prefix ? `${prefix}.${key}` : key] = { note: notes?.[key] }
    return map
  }, {})
}

/**
 * Builds the map from what the backend recorded against a draft.
 *
 * Nothing is inferred here, unlike `prefilledFrom`: a field the borrower
 * typed over is absent even though it still holds a value.
 */
export function prefilledSources(
  sources: Record<string, string> | null | undefined,
  { prefix }: { prefix?: string } = {},
): PrefilledMap {
  const keys = Object.keys(sources ?? {})

  return Object.fromEntries(
    keys.map((key) => [prefix ? `${prefix}.${key}` : key, {}]),
  )
}

/** What, if anything, was prefilled for one field. */
export function usePrefilledField(name: string) {
  const entry = useContext(PrefilledFieldsContext)[name]

  return { isPrefilled: !!entry, note: entry?.note }
}
